import type { PDFDocumentProxy } from 'pdfjs-dist'

/** A sheet of a converted workbook: its name and the PDF pages it covers (1-based). */
export interface SheetTab {
  name: string
  pages: number[]
}

/** Where a sheet starts, as the PDF's outline records it. */
export interface SheetStart {
  name: string
  page: number
}

/**
 * The sheets behind `starts`, each running up to the next one's first page —
 * one page in the one-page-per-sheet layout, several in print layout. Null
 * when the starts do not describe the whole document in order, so nothing is
 * left out of the preview.
 */
export function sheetTabs(starts: SheetStart[], numPages: number): SheetTab[] | null {
  if (starts.length === 0 || starts[0].page !== 1) return null
  for (let i = 1; i < starts.length; i++) {
    if (starts[i].page <= starts[i - 1].page) return null
  }
  if (starts[starts.length - 1].page > numPages) return null
  return starts.map((start, i) => {
    const end = i + 1 < starts.length ? starts[i + 1].page : numPages + 1
    return {
      name: start.name,
      pages: Array.from({ length: end - start.page }, (_, offset) => start.page + offset),
    }
  })
}

/**
 * The sheet starts from the PDF's top-level bookmarks: LibreOffice writes one
 * per sheet, named after it and pointing at its first page.
 */
export async function readSheetStarts(pdf: PDFDocumentProxy): Promise<SheetStart[]> {
  const outline = await pdf.getOutline()
  if (!outline) return []
  const starts: SheetStart[] = []
  for (const item of outline) {
    const dest = typeof item.dest === 'string' ? await pdf.getDestination(item.dest) : item.dest
    const target = dest?.[0]
    if (target === undefined || target === null) return []
    const index = typeof target === 'number' ? target : await pdf.getPageIndex(target)
    starts.push({ name: item.title, page: index + 1 })
  }
  return starts
}
